/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState } from "react";
import { Eraser } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { useGetImportFile, useImportUsers } from "@/services/importUsers";

    
export default function Index() {
    const [loading, setLoading] = useState(false);
    const [networkError, setNetworkError] = useState(null);
    const [importData, setImportData] = useState(null);
    const [importFinished, setImportFinished] = useState(false);

    const {getImportFile} = useGetImportFile()
    const {importUsers} = useImportUsers()

    const importFileInput = useRef(null);

    const onGetImportFile = async () => {
        setNetworkError(null)
        try {
            const template = await getImportFile()
            console.log(template)

            // create blob from tar.gz and download it
            const url = URL.createObjectURL(template.data);
            const link = document.createElement('a');
            link.href = url;
            // Get the file name from the header
            const fileName = template.headers['content-disposition'].split('filename=')[1].split(';')[0]
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove()
        } catch (e) {
            if (isAxiosError(e)) {
                setNetworkError(axiosErrorToStringError(e));    
            } else {
                console.log(e)
                setNetworkError(e.message);
            }
        }

    }

    const onFileChange = async (file) => {
        setLoading(true)
        setNetworkError(null)
        try {
            const data = await importUsers(file, true)
            setImportData(data)
        }
        catch (e) {
            console.log(e)

            if(isAxiosError(e) && e.response?.status === 400) {
                setNetworkError(e.response.data.detail)
            } else if (isAxiosError(e)) {
                setNetworkError(e.message)
            } else {
                setNetworkError(e.message)
            }
        }
        setLoading(false)
    }

    const onImportUsers = async () => {
        setLoading(true)
        setNetworkError(null)
        try {
            const file = importFileInput.current.files[0]
            const data = await importUsers(file, false)
            setImportData(data)

        } catch (e) {
            if (isAxiosError(e)) {
                setNetworkError(axiosErrorToStringError(e));    
            } else {
                console.log(e)
                setNetworkError(e.message);
            }
        }
        setLoading(false)
        setImportFinished(true)
    }


    return (
        <>
            <p className="text-sm text-secondary mt-2">
                Cette page permet d&apos;importer des utilisateurs dans la base du forum depuis un fichier xlsx.<br />
                L&apos;import correspond à ce que ferait la plateforme d&apos;inscription : ajout d&apos;utilisateurs, ajout de rôles, modification d&apos;email.<br />
                L&apos;import ne réalise <span className="font-bold">pas</span> les opérations suivantes : suppression d&apos;utilisateurs, suppression de rôles<br />
                Lorsqu&apos;un utilisateur est ajouté, un email de bienvenue est automatiquement envoyé à l&apos;utilisateur avec son mot de passe.<br />
                Si l&apos;utilisateur existe déjà, le rôle est ajouté mais aucun mail n&apos;est envoyé.<br />
                Le fichier de template est disponible en cliquant sur le bouton ci-dessous. Le format doit être respecté, les colonnes ne doivent ni être déplacées, ni supprimées, ni ajoutées.
                <br />
                <button className="underline text-primary" onClick={onGetImportFile}>
                    Télécharger le fichier de template
                </button>
            </p>

            <h2 className="text-xl font-bold my-6">Import des utilisateurs</h2>

            <div className="flex flex-row justify-between w-full">
                <div className="flex flex-col w-[40%] border border-gray-400 p-3 gap-2 items-baseline">
                    <div className="flex flex-row gap-2">
                        <input type="file" accept=".xlsx" id="file" ref={importFileInput} className="border border-gray-300 rounded-sm px-2 py-0 text-sm" placeholder="Choisissez un fichier de template" onChange={(e) => onFileChange(e.target.files[0])} />
                        <button className="disabled:text-gray-300" disabled={loading || !importFileInput.current || !importFileInput.current.value || importFileInput.current.value == "" } onClick={() => {importFileInput.current.value = null; setImportData(null); setImportFinished(false); setNetworkError(null)}}><Eraser className="" size={16}/></button>
                    </div>
                    <div className="flex flex-col w-full items-center">
                        { networkError && <span className="text-red-500 text-sm">{networkError}</span> }
                    </div>
                </div>
                {
                    importData && <div className="flex flex-col w-[50%] border border-gray-400 p-3 gap-2">
                        <h3 className="text-md font-bold">Vérification du fichier avant import</h3>
                        <ul className="text-sm">
                            <li>Nombre de résultats : {importData.length}</li>
                            <li>Nombre d&apos;erreurs : {importData.filter(x => x.error).length}</li>
                            <li>Nombre de lignes inutiles (utilisateurs déjà présents avec le même rôle): {importData.filter(x => x.nothing_done).length}</li>
                        </ul>
                        { ! importFinished && 
                            ( 
                                (importData.length > 0 && importData.length != importData.filter(x => x.nothing_done).length) && <button className="border border-primary rounded-md px-2 py-1 text-sm bg-blue-200 hover:bg-blue-400" onClick={onImportUsers}>Importer les utilisateurs</button> || <span className="text-sm text-secondary text-center">Aucun utilisateur à importer</span>
                            )
                            || <span className="text-sm text-green-500 text-center">Import terminé</span>
                        }
                    </div>
                }
            </div>

            <div className="flex flex-col mt-6 text-primary text-sm">
                <table className="w-full border">
                    <thead>
                        <tr className="border">
                            <th className="text-left">#</th>
                            <th className="text-left">Prénom</th>
                            <th className="text-left">Nom</th>
                            <th className="text-left">Email</th>
                            <th className="text-left">Role</th>
                            <th className="text-left">Region</th>
                            <th className="text-left">Métier</th>
                            <th className="text-left">Erreur</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            importData && importData.length > 0 && importData.map((x, i) => <tr key={i} className={`${x.error && "bg-red-100"} ${x.nothing_done && "text-gray-400"} border`}>
                                <td className="text-left">{i+2}</td>
                                <td className="text-left">{x.first_name}</td>
                                <td className="text-left">{x.last_name}</td>
                                <td className="text-left">{x.email}</td>
                                <td className="text-left">{x.role_type}</td>
                                <td className="text-left">{x.region_code}</td>
                                <td className="text-left">{x.skill_code}</td>
                                <td className="text-left">{x.error}</td>
                            </tr>) || ( 
                                importData && importData.length == 0 && <tr><td colSpan={8} className="text-center">Aucun résultat</td></tr> 
                                || <tr><td colSpan={8} className="text-center">Veuillez sélectionner un fichier pour voir les résultats</td></tr>
                            )
                        }
                    </tbody>
                </table>
            </div>
        </>
    )
}